import React, { useState, useRef, useEffect } from 'react';
import { KeenIcon } from '@/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faArrowUpRightFromSquare, faBars, faLightbulb, faUpRightAndDownLeftFromCenter, faDownLeftAndUpRightToCenter, faHeart, faThumbsDown, faCopy } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '@/hooks/useAuth';
import { useGetAgent } from '@/hooks/useAgent';

const AgentChatPreview = ({
  name,
  introduction,
  greeting,
  fill,
  stroke,
  suggest,
  suggestOverlay,
  button,
  buttonOverlay,
  font,
  font_styling,
  font_size,
  heart,
  dislike,
  copy
}) => {
  const suggestions = [
    "Đăng ký khám bệnh",
    "Chạy thận nhân tạo",
    // "Thời gian làm việc của bệnh viện"
  ];
  const [isIntroExpanded, setIsIntroExpanded] = useState(false);
  const [isIntroOverflow, setIsIntroOverflow] = useState(false);
  const {userData} = useAuth()
  const { data: agent, isLoading } = useGetAgent(userData?.uID);
  const introRef = useRef(null);

  useEffect(() => {
    if (introRef.current) {
      setIsIntroOverflow(introRef.current.scrollWidth > introRef.current.clientWidth);
    }
  }, [introduction]);

  // Xử lý font style
  let fontWeight = 'normal', fontStyle = 'normal', textDecoration = 'none';
  if (font_styling) {
    if (font_styling.toLowerCase().includes('bold')) fontWeight = 'bold';
    if (font_styling.toLowerCase().includes('italic')) fontStyle = 'italic';
    if (font_styling.toLowerCase().includes('underline')) textDecoration = 'underline';
  }

  const typographyStyle = {
    fontFamily: font,
    fontWeight,
    fontStyle,
    textDecoration,
    fontSize: font_size ? font_size + 'px' : undefined
  };

  return (
    <div className="flex-1 bg-white flex flex-col rounded-t-xl max-w-[450px] rounded-xl" style={typographyStyle}>
      {/* header chat */}
      <div className=' rounded-t-xl' style={{ background: stroke }}>
        <div className='py-2 px-5 flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <img src={agent?.data?.bot_icon || '/media/images/logo_mini_chatme.png'} alt="logo agent" className='size-8' />
            <p className='text-white'>{name}</p>
          </div>
          <div className='flex items-center gap-3'>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="fa-flip-horizontal text-white size-4" />
            <FontAwesomeIcon icon={faAngleDown} className='text-white' />
          </div>
        </div>
      </div>
      {/* body chat */}
      <div className='bg-white px-5 pt-4 flex-1'>
        {/* intro and greeting */}
        {(introduction || greeting) && (
          <div className="flex flex-col gap-2 pb-3 border-b">
            {introduction && (
              <div className='w-full border rounded-md flex items-start justify-between px-5 py-5' style={{ background: fill }}>
                <div
                  ref={introRef}
                  className={`flex-1 ${isIntroExpanded ? '' : 'line-clamp-2'}`}
                  style={{
                    whiteSpace: 'pre-line',
                    wordBreak: isIntroExpanded ? 'break-word' : undefined
                  }}
                  dangerouslySetInnerHTML={{ __html: introduction }}
                />
                <FontAwesomeIcon
                  icon={isIntroExpanded ? faDownLeftAndUpRightToCenter : faUpRightAndDownLeftFromCenter}
                  style={{ cursor: 'pointer', marginLeft: 8, marginTop: 4 }}
                  onClick={() => setIsIntroExpanded((prev) => !prev)}
                />
              </div>
            )}
            {greeting && (
              <div 
                className='rounded-md px-5 py-3' 
                style={{ background: fill, whiteSpace: 'pre-line' }}
                dangerouslySetInnerHTML={{ __html: greeting }}
              />
            )}
          </div>
        )}

        {/* chat bot */}
        <div className='flex items-center gap-3 pt-3 relative'>
          <img src="/media/images/logo_agent.png" alt="logo agent" className='size-8' />
          <div className='py-2 w-full border rounded-md flex items-center relative' style={{ background: fill }}>
            <p className='px-5'>
              Xin chào Anh/Chị, Em là Chatme - trợ lý chăm sóc khách hàng , rất vui được hỗ trợ Anh/Chị ạ.
            </p>
            {(!!heart || !!dislike || !!copy) && (
              <div className="flex gap-3 absolute right-5 bottom-0 translate-y-1/2">
                {!!heart && <FontAwesomeIcon icon={faHeart} className="text-red-500 p-1 bg-gray-200 rounded-full" title="Heart" />}
                {!!dislike && <FontAwesomeIcon icon={faThumbsDown} className="text-blue-500 p-1 bg-gray-200 rounded-full" title="Dislike" />}
                {!!copy && <FontAwesomeIcon icon={faCopy} className="text-gray-500 p-1 bg-gray-200 rounded-full" title="Copy" />}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* footer chat */}
      <div className='bg-white p-5 rounded-b-xl'>
        <div className='flex items-center gap-2 mb-6'>
          {suggestions.map((text, idx) => (
            <div key={idx} className='flex items-center gap-2 w-fill border rounded-3xl px-2' style={{ background: suggestOverlay, color: suggest }}>
              <FontAwesomeIcon icon={faLightbulb} className='size-3' />
              <p className='text-sm'>{text}</p>
            </div>
          ))}
        </div>
        <div className="relative">
          <span className="absolute -top-3 left-12 -translate-x-1/2 text-gray-400 text-sm bg-white px-1">0/500</span>
          <div className='border border-1 flex items-center justify-between rounded-md'>
            <div className=' border rounded-full flex justify-center items-center m-2' style={{ background: buttonOverlay }}>
              <FontAwesomeIcon icon={faBars} className='size-4 p-2' style={{ color: button }} />
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              version="1.0"
              viewBox="0 0 112 112"
              className="size-7 mr-2"
              style={{ color: button }}
            >
              <path
                fill="currentColor"
                d="M54.4 16.6c-27.9 9-51.5 17-52.2 17.8-1.8 1.8-1.9 6.4-.1 8.1.8.7 10.3 
                  4.2 21.2 7.8l19.9 6.6 18-12.5c9.9-6.9 18.2-12.3 18.5-12.1.2.3-5.2 8.6-12.1 18.4L55 68.7l6.6 
                  19.9c3.7 10.9 7.2 20.5 7.9 21.3 1.7 1.8 6.3 1.7 8.1-.1 1.7-1.6 33.6-100.1 33.6-103.5.1-2.7-1.6
                  -4.9-4.1-5.6-1-.2-24.7 6.9-52.7 15.9"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export { AgentChatPreview };